import { rgba } from 'polished';
import { Doughnut } from 'react-chartjs-2';
import { Box, Flex } from 'reflexbox';
import styled, { useTheme } from 'styled-components';

import { Skeleton, tokens } from '@treams/treams-ui';

const Container = styled(Box)<{ size: number }>`
	flex-grow: 0;
	flex-shrink: 0;
	position: relative;
	width: ${(props) => props.size}px;
	height: ${(props) => props.size}px;
`;

const Graph = styled(Box)`
	position: absolute;
	width: 100%;
	height: 100%;
`;

const Legend = styled(Box)`
	flex-shrink: 1;
`;

const Description = styled(Flex)`
	color: ${tokens.text.secondary};
	font-size: 14px;
	align-items: center;
	padding: 2px 0px;
`;

const Dot = styled(Box)<{ $color: string }>`
	margin-right: 5px;
	width: 4px;
	height: 4px;
	background: ${(props) => props.theme.colors[props.$color]};
	border-radius: 2px;
`;

const Center = styled(Flex)`
	position: absolute;
	top: 50%;
	left: 50%;
	margin: -50px;
	width: 100px;
	height: 100px;
	align-items: center;
	justify-content: center;
	flex-direction: column;
`;

const Value = styled(Box)<{ fontSize: number }>`
	font-size: ${(props) => props.fontSize}px;
	font-weight: bold;
	line-height: 20px;
`;

const Byline = styled(Box)`
	font-size: 8px;
`;

export default function DoughnutMetric(props: {
	data: {
		label?: string;
		value: number;
		color: string;
		hidden?: boolean;
	}[];
	padding?: number;
	size?: number;
	fontSize?: number;
	byline?: string;
	legend?: boolean;
	max?: number;
	digits?: number;
	suffix?: string;
	tooltip?: boolean;
}) {
	const theme = useTheme();

	const data = {
		labels: props.data.map(({ label }) => label),
		datasets: [
			{
				data: props.data.map(({ value }) => value),
				backgroundColor: props.data.map(({ color, hidden }) =>
					hidden ? rgba(theme.colors[color], 0.25) : theme.colors[color],
				),
			},
		],
	};

	const options = {
		responsive: true,
		plugins: {
			legend: {
				display: false,
			},
			tooltip: {
				enabled: props.tooltip ?? true,
			},
		},
		maintainAspectRatio: false,
	};

	return (
		<Flex p={props.padding ?? 3} justifyContent="space-between" alignItems="center">
			<Container size={props.size ?? 122}>
				<Center>
					<Value fontSize={props.fontSize ?? 22}>
						{(
							props.data.reduce((p, { value, hidden }) => p + (hidden ? 0 : value), 0) *
							(props.max ?? 1)
						).toFixed(props.digits ?? 0)}
						{props.suffix}
					</Value>
					{props.byline && <Byline>{props.byline}</Byline>}
				</Center>
				<Graph>
					<Doughnut data={data} options={options} width={122} height={122} />
				</Graph>
			</Container>
			{props.legend !== false && (
				<Legend>
					{props.data.map(({ label, color }) => (
						<Description key={label}>
							<Dot $color={color} />
							{label}
						</Description>
					))}
				</Legend>
			)}
		</Flex>
	);
}

const SkeletonContainer = styled(Skeleton.Box)<{ size: number }>`
	display: flex;
	align-items: center;
	justify-content: center;
	width: ${(props) => props.size}px;
	height: ${(props) => props.size}px;
	border-radius: ${(props) => props.size / 2}px;
`;

const SkeletonCenter = styled(Flex)<{ size: number }>`
	width: ${(props) => (props.size * 0.5).toFixed()}px;
	height: ${(props) => (props.size * 0.5).toFixed()}px;
	border-radius: ${(props) => (props.size / 2).toFixed()}px;
	background: white;
	align-items: center;
	justify-content: center;
`;

function DoughnutMetricSkeleton(props: {
	padding?: number;
	size?: number;
	fontSize?: number;
	animated?: boolean;
}) {
	return (
		<Box p={props.padding ?? 3}>
			<SkeletonContainer size={props.size ?? 122} animated={props.animated}>
				<SkeletonCenter size={props.size ?? 122}>
					<Value fontSize={props.fontSize ?? 22}>-</Value>
				</SkeletonCenter>
			</SkeletonContainer>
		</Box>
	);
}

DoughnutMetric.Skeleton = DoughnutMetricSkeleton;
